import React from 'react';
import CardCommon from '@sg/components/CardCommon';
import { rendTitle } from '@sg/utils/commonPageMethod';
import { Justify, Button } from 'tea-component';
import RenderOperations from '@sg/components/Chart/Operations';
import { pickOperations } from '@sg/commonData/pickOperations';
import { mathFormat } from '@sg/utils/common';
import Chart from '@sg/components/Chart';
import { xAxisHighLight, applyValueAxisBoundaryGap } from '@sg/utils/chatUtils';
import TableContent from '@sg/components/TableContent';
import { scrollable } from 'tea-component/lib/table/addons';
import Loading from '@intelligence/components/Loading';
import AutoSaveModal from '@/pages/strategic/components/AutoSaveModal';
import RoutePrompt from '@/pages/strategic/components/RoutePrompt';
import { appendStackedTotalLabelSeries } from '@/pages/strategic/utils/stackedTotalLabel';
import { height, showRevenueTrendList } from '../../../../pageConfig';
import { useLifecycleFinancialForecast } from './hooks/useLifecycleFinancialForecast';

export interface ILifecycleFinancialForecastCardProps {
  projectId: string;
  selectedMonth?: string;
  studioId?: string;
  projectInfo: string;
  canEdit: boolean;
  onEditingChange?: (isEditing: boolean) => void;
}

const LifecycleFinancialForecastCard: React.FC<
  ILifecycleFinancialForecastCardProps
> = ({
  projectId,
  selectedMonth,
  studioId,
  projectInfo,
  canEdit,
  onEditingChange,
}) => {
  const {
    showChatsType,
    lifecycleUpdateTime,
    lifecycleData,
    lifecycleTableLoading,
    originalLifecycleData,
    copySalesForecast,
    netRevenueForecast,
    allData,
    DCTRef,
    DCTOperationsRef,
    isDCTEditing,
    lifecycleAutoSaveVisible,
    handleLifecycleAutoSaveConfirm,
    handleLifecycleAutoSaveCancel,
    clearLifecycleAutoSave,
    hasLifecycleUnsavedChanges,
    handleLifecycleFinancialForecastChange,
    handleDCTEditingChange,
    handleDCTEditClick,
    updateLifecycleFinancialForecast,
    fetchLifecycleFinancialForecast,
    formatMessage,
  } = useLifecycleFinancialForecast({
    projectId,
    selectedMonth,
    studioId,
    projectInfo,
    onEditingChange,
  });

  return (
    <>
      <CardCommon
        title={rendTitle({
          title: `${formatMessage({ id: 'dbs_life_cycle_financial_forecast' })} ($)`,
          tips: lifecycleUpdateTime
            ? `${formatMessage({ id: 'dbs_update_on' })} ${lifecycleUpdateTime}`
            : '',
        })}
        operation={
          <>
            <RenderOperations
              ref={DCTOperationsRef}
              defaultValue={showChatsType.value}
              defaultValueShow={showChatsType.format}
              change={handleLifecycleFinancialForecastChange}
              options={pickOperations(
                ['view', ...showRevenueTrendList, 'download'],
                formatMessage,
              )}
              downloadParams={{
                ...allData.table,
                name: formatMessage({ id: 'dbs_life_cycle_financial_forecast' }),
              }}
            />
            {canEdit &&
              lifecycleData.length > 0 &&
              (!isDCTEditing ? (
                <div
                  className="dbs-weekly-buttons-wrapper"
                  style={{ marginLeft: '10px' }}
                >
                  <Button
                    onClick={handleDCTEditClick}
                    style={{ height: '30px', minWidth: '60px', fontSize: '12px' }}
                    className="dbs-gradient-btn"
                  >
                    {formatMessage({ id: 'dbs_edit' })}
                  </Button>
                </div>
              ) : (
                <div
                  className="dbs-weekly-buttons-wrapper"
                  style={{ marginLeft: '10px' }}
                >
                  <Button
                    onClick={() => DCTRef.current?.handleSave()}
                    style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                    className="dbs-gradient-btn"
                  >
                    {formatMessage({ id: 'dbs_confirm' })}
                  </Button>
                  <Button
                    onClick={() => {
                      clearLifecycleAutoSave();
                      DCTRef.current?.handleCancel();
                    }}
                    style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                    className="dbs-outline-btn"
                  >
                    {formatMessage({ id: 'dbs_cancel' })}
                  </Button>
                </div>
              ))}
          </>
        }
      >
        <Justify
          bottom
          left={
            showChatsType?.value !== 'table' ? (
              <div className="dbs-lifecycle-forecast-wrapper">
                {/* Copy Sales Forecast Section */}
                <div className="dbs-lifecycle-forecast-section">
                  <div className="dbs-lifecycle-forecast-section__title">
                    Copy Sales Forecast
                  </div>
                  <div className="dbs-lifecycle-forecast-section__items">
                    {(copySalesForecast && copySalesForecast.length > 0
                      ? copySalesForecast.slice(0, 3)
                      : [
                          { year: '', value: null },
                          { year: '', value: null },
                          { year: '', value: null },
                        ]
                    ).map((item: any, index: number) => (
                      <div
                        className="dbs-lifecycle-forecast-item"
                        key={`copy-sales-${index}`}
                      >
                        <div className="dbs-lifecycle-forecast-item__header">
                          <span className="dbs-lifecycle-forecast-item__year-label">
                            Y{index + 1}
                          </span>
                          {item.year && (
                            <span className="dbs-lifecycle-forecast-item__year">
                              {' '}
                              ({item.year})
                            </span>
                          )}
                        </div>
                        <div className="dbs-lifecycle-forecast-item__value">
                          {mathFormat(item.value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Gross Revenue Forecast Section */}
                <div className="dbs-lifecycle-forecast-section">
                  <div className="dbs-lifecycle-forecast-section__title">
                    Gross Revenue Forecast
                  </div>
                  <div className="dbs-lifecycle-forecast-section__items">
                    {(netRevenueForecast && netRevenueForecast.length > 0
                      ? netRevenueForecast.slice(0, 3)
                      : [
                          { year: '', value: null },
                          { year: '', value: null },
                          { year: '', value: null },
                        ]
                    ).map((item: any, index: number) => (
                      <div
                        className="dbs-lifecycle-forecast-item"
                        key={`net-revenue-${index}`}
                      >
                        <div className="dbs-lifecycle-forecast-item__header">
                          <span className="dbs-lifecycle-forecast-item__year-label">
                            Y{index + 1}
                          </span>
                          {item.year && (
                            <span className="dbs-lifecycle-forecast-item__year">
                              {' '}
                              ({item.year})
                            </span>
                          )}
                        </div>
                        <div className="dbs-lifecycle-forecast-item__value">
                          {`$${mathFormat(item.value)}`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null
          }
          right={''}
        />
        {showChatsType?.value !== 'table' && lifecycleTableLoading && (
          <Loading style={{ height }} />
        )}
        {!lifecycleTableLoading &&
          showChatsType?.value !== 'table' &&
          allData[showChatsType?.value] && (
            <Chart
              data={allData[showChatsType?.value]}
              // key={`Life Cycle${showChatsType?.value}`} // Commented out to prevent race condition when rapidly switching views
              type={showChatsType?.value}
              height={height}
              setOtherConfigFunc={(opt: any) => {
                opt.grid.containLabel = true;
                const isBarChart = showChatsType?.value === 'bar';
                opt = xAxisHighLight(opt, {
                  type: isBarChart ? 'shadow' : 'line',
                  lineStyle: isBarChart
                    ? {
                        color: 'rgba(216, 218, 228, 0.3)',
                        width: 94,
                        type: 'solid',
                      }
                    : {
                        color: '#BBBDCB',
                        width: 1,
                        type: 'solid',
                      },
                } as any);

                // Reduce axis-pointer hover edge cases in shadow mode.
                if (isBarChart && opt?.tooltip?.axisPointer?.label) {
                  opt.tooltip.axisPointer.label.show = false;
                }

                if (isBarChart) {
                  // Pad y-axis so bar/total labels are not clipped at the chart edge.
                  applyValueAxisBoundaryGap(opt);
                }

                opt = appendStackedTotalLabelSeries(opt, {
                  enabled:
                    !!showChatsType?.format && showChatsType?.value === 'bar',
                  seriesName: '__total_label__',
                  targetSeriesNames: ['Dev Cost', 'Mkt Cost', 'Other Cost'],
                  labelFormatter: (value: number) => mathFormat(value),
                });

                return opt;
              }}
            />
          )}
        {showChatsType?.value === 'table' && allData[showChatsType?.value] && (
          <TableContent
            ref={DCTRef}
            className="dbs-commons-table dbs-chats-table"
            key={`${showChatsType?.value}`}
            emptyMinHeight={height}
            // searchForm={searchForm}
            datas={allData[showChatsType?.value]?.data}
            columns={allData[showChatsType?.value]?.columns}
            editColumns={allData[showChatsType?.value]?.editColumns}
            sort={[{ by: 'fill_time', order: 'desc' }]}
            resultKey="list"
            recordKey="date"
            // loading={tableLoading}
            // preParams={preParams}
            // lastResultData={lastResultData}
            // scroll={{ x: 'max-content', y: 55 * 5 }}
            // handerSearch={true}
            // immediately={false}
            addons={[
              // 支持表格滚动，高度超过 192 开始显示滚动条
              scrollable({
                // maxHeight: '310px',
                // minWidth: 2000,
              }),
            ]}
            editable={true}
            loading={lifecycleTableLoading}
            userPermissions={{ canEdit }}
            hideButtons={true}
            onEditingChange={handleDCTEditingChange}
            onSave={updateLifecycleFinancialForecast}
            onCancelRefetch={fetchLifecycleFinancialForecast}
            originalData={originalLifecycleData}
          />
        )}
      </CardCommon>

      {/* Auto-save Modal for Lifecycle Financial Forecast table */}
      <AutoSaveModal
        visible={lifecycleAutoSaveVisible}
        onConfirm={handleLifecycleAutoSaveConfirm}
        onClose={handleLifecycleAutoSaveCancel}
      />

      {/* RoutePrompt for Lifecycle Financial Forecast table unsaved changes */}
      <RoutePrompt
        when={hasLifecycleUnsavedChanges}
        onOk={async () => {
          // Save Lifecycle Financial Forecast changes before navigating away
          if (DCTRef.current?.handleSave) {
            await DCTRef.current.handleSave();
          }
        }}
      />
    </>
  );
};

export default LifecycleFinancialForecastCard;
